/**
 * TenantSelectionStep Component
 * Step 1 of the deployment wizard - select tenants to deploy to
 */

import React from 'react';
import {
  Text,
  Checkbox,
  Button,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import type { TenantDeployment } from '../types';
import type { TenantSelectionStepProps } from './types';
import { WizardDialog } from '../shared';
import './TenantSelectionStep.css';

export const TenantSelectionStep: React.FC<TenantSelectionStepProps> = ({
  policy,
  localSelectedTenants,
  error,
  getTenantNameById,
  getTenantPrefixById,
  onTenantToggle,
  onNext,
  onClose,
}) => {
  if (!policy) return null;

  return (
    <WizardDialog
      title={`Deploy ${policy.policyName}`}
      onClose={onClose}
      maxWidth="600px"
    >
            <Text className="ts-intro-text">
              Select tenants to deploy this policy to. Tenants where the policy is already deployed can only be selected individually.
            </Text>
            <Text className="ts-intro-text" style={{ display: 'block', marginTop: 6 }}>
              Parent and standalone labels, as well as policies, will be prefixed with each tenant's configured prefix when first created; existing labels are never renamed, and child labels are not prefixed.
            </Text>

            {error && (
              <MessageBar intent="error" style={{ marginBottom: 16 }}>
                <MessageBarBody>{error}</MessageBarBody>
              </MessageBar>
            )}

            <div className="ts-tenant-list">
              {policy.tenantDeployments.map((td: TenantDeployment) => {
                const isDeployed = td.status === 'Deployed';
                const isPartiallyDeployed = td.status === 'PartiallyDeployed';
                const deployedPercent = td.totalRulesCount > 0
                  ? Math.round((td.deployedRulesCount / td.totalRulesCount) * 100)
                  : 0;

                const badgeType = isDeployed ? 'deployed' : isPartiallyDeployed ? 'partial' : 'notDeployed';
                const badgeText = isDeployed ? 'Deployed' : isPartiallyDeployed ? 'Partially Deployed' : 'Not Deployed';
                const badgeClass = badgeType === 'notDeployed' ? 'ts-badge--not-deployed' : `ts-badge--${badgeType}`;
                const prefix = getTenantPrefixById?.(td.tenantId) ?? '';

                return (
                  <div key={td.tenantId} className="ts-tenant-row">
                    <Checkbox
                      checked={localSelectedTenants.has(td.tenantId)}
                      onChange={(_, data) => onTenantToggle(td.tenantId, !!data.checked)}
                      aria-label={`Select tenant ${td.tenantName}`}
                      input={{ 'aria-label': `Select tenant ${td.tenantName}` }}
                    />
                    <div className="ts-tenant-info">
                      <div className="ts-tenant-name" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {getTenantNameById(td.tenantId)}
                        <span
                          style={{
                            padding: '1px 6px',
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 700,
                            background: '#eff6fc',
                            color: '#0078d4',
                            border: '1px solid #0078d4',
                            whiteSpace: 'nowrap',
                          }}
                          title="Parent and standalone labels are deployed with this prefix; child labels are not prefixed."
                        >
                          Prefix: {prefix || '—'}
                        </span>
                      </div>
                      <div className="ts-tenant-status">
                        {td.deployedRulesCount}/{td.totalRulesCount} rules deployed ({deployedPercent}%)
                      </div>
                    </div>
                    <span className={`ts-badge ${badgeClass}`}>{badgeText}</span>
                  </div>
                );
              })}
            </div>

            <div className="ts-footer">
              <Button appearance="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                disabled={localSelectedTenants.size === 0}
                onClick={onNext}
              >
                Next
              </Button>
            </div>
    </WizardDialog>
  );
};

export default TenantSelectionStep;
